import { useForceUpdate } from '@kk/shared/hooks/useForceUpdate';
import { InlineNotification } from '@kk/ui/components/Notifications/InlineNotification';
import { TopLineBanner } from '@kk/ui/components/Notifications/TopLineBanner';
import { AppInsightsErrorBoundary as MicrosoftAppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { appInsightsPlugin } from '@/app-insights';

class ErrorBoundaryFallback extends React.Component<{
  onError: (message?: string) => React.ReactElement;
  children: React.ReactElement;
}> {
  state = { hasError: false, message: '' };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, message: error.message };
  }

  render() {
    if (this.state.hasError) {
      return this.props.onError(this.state.message);
    }

    return this.props.children;
  }
}

export function ErrorBoundary({
  children,
  inline = true,
  className,
}: {
  children: React.ReactElement;
  inline?: boolean;
  className?: string;
}) {
  const forceUpdate = useForceUpdate();
  const { reset } = useQueryErrorResetBoundary();
  const { t } = useTranslation();

  const renderError = () => {
    if (inline) {
      return (
        <InlineNotification
          data-testid="inline-error"
          type="danger"
          size="medium"
          headline={t('error.component.title')}
          message={t('error.component.message')}
          className={className}
          onDismiss={() => {
            reset();
            forceUpdate();
          }}
        />
      );
    }

    const toplineBanner = (
      <TopLineBanner
        data-testid="error-banner"
        type="danger"
        message={t('error.api.title')}
        label={t('error.api.message')}
        className={className}
        onDismiss={() => {
          reset();
          window.location.reload();
        }}
      />
    );

    const portalTarget = document.getElementById('error-portal');
    return portalTarget
      ? createPortal(toplineBanner, portalTarget)
      : toplineBanner;
  };

  if (import.meta.env.PROD) {
    return (
      <MicrosoftAppInsightsErrorBoundary
        appInsights={appInsightsPlugin}
        onError={renderError}
      >
        {children}
      </MicrosoftAppInsightsErrorBoundary>
    );
  }

  return (
    <ErrorBoundaryFallback onError={renderError}>
      {children}
    </ErrorBoundaryFallback>
  );
}
